import React, { useState, useMemo, useRef } from 'react';
import { Table, InputNumber, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectFilteredMachines,
  selectMachineLoading,
  selectfetchMachineLoading,
} from './store/selectors';
import { showModal } from 'Core/redux/main/slice';
import { deleteMachine } from './store/slice';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
import { actions as machineActions } from './store/slice';

const { setMachine } = machineActions;

const { CELL_ADD_EDIT } = MODAL_TYPES;

// Helper: extract integer from values like "430mm", "246g", etc.
const extractInteger = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const stringValue = String(value).trim();
  if (!stringValue) return '';
  const match = stringValue.match(/(\d+(?:\.\d+)?)/);
  if (match && match[1]) {
    const num = parseFloat(match[1]);
    if (!isNaN(num)) {
      return Math.floor(num).toString();
    }
  }
  return '';
};

// Helper: safely convert any value to string for comparison (handles null/undefined)
const safeStr = (val) => (val != null ? String(val) : '');

// Helper: safely extract number for numeric sorting
const safeNum = (val) => parseInt(extractInteger(val)) || 0;

// Helper: convert mm to inches (1 inch = 25.4 mm, so 1 mm = 1/25.4 inches)
const mmToInches = (mm) => {
  const num = safeNum(mm);
  // Use division for exact inverse conversion
  return num / 25.4;
};

// Helper: convert inches to mm (1 inch = 25.4 mm)
const inchesToMm = (inches) => {
  const num = typeof inches === 'number' ? inches : Number(inches);
  if (isNaN(num) || !isFinite(num)) return 0;
  return num * 25.4;
};

// Helper: format number with unit
const formatWithUnit = (value, unit) => {
  if (value === null || value === undefined || value === '') return '';
  const num = safeNum(value);
  if (num === 0 && value !== 0) return '';
  const displayValue = unit === 'inches' ? mmToInches(num) : num;
  // Round to 2 decimal places for inches, no decimals for mm
  const formatted = unit === 'inches' ? displayValue.toFixed(2) : displayValue.toString();
  return formatted;
};

const MachineTable = ({ unit = 'mm' }) => {
  const dispatch = useDispatch();
  // Use separate selectors to avoid creating new objects on each render
  const filteredData = useSelector(selectFilteredMachines);
  const isLoading = useSelector(state => selectMachineLoading(state) || selectfetchMachineLoading(state));

  // State to manage filter values for each column (controlled by Ant Design)
  const [filterStates, setFilterStates] = useState({});
  // Ref to store filter values for immediate access in onFilter
  const filterStatesRef = useRef({});

  // Manually filter the data based on filterStates
  const filteredTableData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return [];
    }
    
    // Columns that need unit conversion for filtering
    const unitConversionColumns = ['tieBarDistanceH', 'tieBarDistanceV', 'moldheightMin', 'moldheightMax'];
    
    const filtered = filteredData.filter(record => {
      // Check each active filter
      return Object.entries(filterStates).every(([dataIndex, filterValue]) => {
        // If no filter value, show the record
        if (filterValue == null || filterValue === undefined || filterValue === '') {
          return true;
        }
        
        // Convert filter value to number
        // NOTE: filterValue is ALWAYS stored in mm (converted when saving)
        // So we don't need to convert it again here, regardless of current unit
        const numFilter = typeof filterValue === 'number' ? filterValue : Number(filterValue);
        if (isNaN(numFilter) || !isFinite(numFilter)) {
          return true;
        }
        
        // Get the record value (always in mm)
        const recordValue = safeNum(record && record[dataIndex] ? record[dataIndex] : null);
        
        // For unit conversion columns, compare in the display unit to match what user sees
        if (unitConversionColumns.includes(dataIndex) && unit === 'inches') {
          // Convert both values to inches and round to 2 decimals (as displayed)
          const recordInInches = Math.round((recordValue / 25.4) * 100) / 100;
          const filterInInches = Math.round((numFilter / 25.4) * 100) / 100;
          // Compare in inches (matching display) - this ensures what user sees matches filter
          return recordInInches >= filterInInches;
        }
        
        // For non-unit conversion columns or when unit is mm, compare directly in mm
        // Round filter value to avoid floating point precision issues
        const roundedFilterValue = Math.round(numFilter * 100) / 100;
        return recordValue >= roundedFilterValue;
      });
    });
    
    // Sort filtered results by the first active filter column (ascending order)
    if (filtered.length > 0 && Object.keys(filterStates).length > 0) {
      const firstFilterColumn = Object.keys(filterStates)[0];
      filtered.sort((a, b) => {
        const aValue = safeNum(a && a[firstFilterColumn] ? a[firstFilterColumn] : null);
        const bValue = safeNum(b && b[firstFilterColumn] ? b[firstFilterColumn] : null);
        return aValue - bValue;
      });
    }
    
    return filtered;
  }, [filteredData, filterStates, unit]);

  const handleEditClick = record => {
    dispatch(setMachine(record));
    dispatch(showModal(CELL_ADD_EDIT, record));
  };

  const handleDeleteClick = id => {
    dispatch(deleteMachine(id));
  };

  // Create filter dropdown for numeric columns (>= filter)
  const getNumericFilterProps = (dataIndex) => {
    // Columns that need unit conversion
    const unitConversionColumns = ['tieBarDistanceH', 'tieBarDistanceV', 'moldheightMin', 'moldheightMax'];
    const needsUnitConversion = unitConversionColumns.includes(dataIndex);
    
    return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      // Get the display value: always convert from stored mm value to display unit
      // NOTE: stored value is always in mm, we need to convert it to display unit
      const storedValue = filterStates[dataIndex];
      
      // Track if user is actively typing (when selectedKeys changes due to user input)
      // We'll use a ref to track the last user input vs. Ant Design's initialization
      const getDisplayValue = () => {
        // If user is actively typing (selectedKeys exists and value is reasonable for display unit)
        if (selectedKeys && selectedKeys.length > 0 && selectedKeys[0] != null) {
          const keyValue = selectedKeys[0];
          
          // Check if this looks like a mm value that was passed by Ant Design
          // (mm values are typically > 100, inches values are typically < 100)
          if (needsUnitConversion && unit === 'inches') {
            // If value is > 100, it's likely in mm and needs conversion
            if (keyValue > 100) {
              // Convert mm to inches
              const inchesValue = mmToInches(keyValue);
              return parseFloat(inchesValue.toFixed(2));
            }
            // Value is already in inches (user typed it)
            return keyValue;
          }
          
          // For mm unit or non-conversion columns, use directly
          return keyValue;
        }
        
        // No selectedKeys, convert stored value to display unit
        if (storedValue == null) return null;
        
        if (needsUnitConversion && unit === 'inches') {
          // Convert stored mm value to inches for display
          const mmValue = typeof storedValue === 'number' ? storedValue : Number(storedValue);
          const inchesValue = mmToInches(mmValue);
          // Round to 2 decimal places for clean display
          return parseFloat(inchesValue.toFixed(2));
        }
        return typeof storedValue === 'number' ? storedValue : Number(storedValue);
      };
      
      const displayValue = getDisplayValue();
      
      // Initialize selectedKeys with converted value when dropdown opens
      // Check if selectedKeys needs to be updated (empty or contains mm value when unit is inches)
      if (displayValue != null) {
        const currentKeyValue = selectedKeys && selectedKeys.length > 0 ? selectedKeys[0] : null;
        const needsUpdate = !currentKeyValue || 
          (needsUnitConversion && unit === 'inches' && currentKeyValue > 100);
        
        if (needsUpdate) {
          setSelectedKeys([displayValue]);
        }
      }
      const handleFilter = () => {
        try {
          // Ensure we have a valid value before confirming
          if (selectedKeys && selectedKeys.length > 0 && selectedKeys[0] != null) {
            let filterValue = selectedKeys[0];
            
            // If filtering a column that uses unit conversion and current unit is inches,
            // convert the filter value from inches to mm for storage
            if (needsUnitConversion && unit === 'inches') {
              filterValue = inchesToMm(filterValue);
              // Round to 2 decimal places to avoid floating point precision issues
              filterValue = Math.round(filterValue * 100) / 100;
            }
            
            // Update state and ref
            setFilterStates(prev => {
              const newState = {
                ...prev,
                [dataIndex]: filterValue
              };
              filterStatesRef.current[dataIndex] = filterValue;
              return newState;
            });
            
            // Use setTimeout to ensure state update is processed before confirm
            setTimeout(() => {
              confirm();
            }, 0);
          } else {
            // If no value, clear the filter
            setFilterStates(prev => {
              const newStates = { ...prev };
              delete newStates[dataIndex];
              delete filterStatesRef.current[dataIndex];
              return newStates;
            });
            clearFilters();
            setTimeout(() => {
              confirm();
            }, 0);
          }
        } catch (error) {
          console.error('Filter error:', error);
        }
      };
      
      const handleReset = () => {
        setFilterStates(prev => {
          const newStates = { ...prev };
          delete newStates[dataIndex];
          delete filterStatesRef.current[dataIndex];
          return newStates;
        });
        clearFilters();
        setTimeout(() => {
          confirm();
        }, 0);
      };

      return (
        <div style={{ padding: 8 }}>
          <p style={{ marginBottom: 6, fontSize: 12, color: '#666' }}>
            Show values <strong>greater than or equal to</strong> ({needsUnitConversion ? unit : ''}):
          </p>
          <InputNumber
            placeholder={`Enter value in ${needsUnitConversion ? unit : ''}`}
            value={displayValue}
            onChange={(val) => {
              // Store exactly what user types - no conversion or formatting
              // This allows free typing of decimals like 36.5
              setSelectedKeys(val != null && val !== undefined ? [val] : []);
            }}
            onPressEnter={handleFilter}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            min={0}
            step={needsUnitConversion && unit === 'inches' ? 0.01 : 1}
            decimalSeparator="."
            controls={false}
          />
          <Space>
            <Button
              type="primary"
              onClick={handleFilter}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={handleReset}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered) => {
      // Check if filter is active from our state (since we're filtering manually)
      const isFilterActive = filterStates[dataIndex] != null;
      return (
        <SearchOutlined 
          style={{ 
            color: (filtered || isFilterActive) ? '#1677ff' : undefined 
          }} 
        />
      );
    },
    filteredValue: filterStates[dataIndex] != null ? [filterStates[dataIndex]] : null,
    onFilter: (value, record) => {
      // Since we're filtering manually via filteredTableData, 
      // we still need onFilter for Ant Design to recognize the filter is active
      // Return true for all records since filtering is handled by filteredTableData
      return true;
    },
    };
  };

  const columns = useMemo(() => [
    {
      title: 'Asset Number',
      dataIndex: 'assetNumber',
      filteredValue: null,
      sorter: (a, b) => safeStr(a.assetNumber).localeCompare(safeStr(b.assetNumber)),
    },
    {
      title: 'Cell Number',
      dataIndex: 'cellNumber',
      filteredValue: null,
      sorter: (a, b) => safeStr(a.cellNumber).localeCompare(safeStr(b.cellNumber)),
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      filteredValue: null,
      sorter: (a, b) => safeStr(a.brand).localeCompare(safeStr(b.brand)),
    },
    {
      title: 'Model Number',
      dataIndex: 'modelNumber',
      filteredValue: null,
      sorter: (a, b) => safeStr(a.modelNumber).localeCompare(safeStr(b.modelNumber)),
    },
    {
      title: 'Tonnage',
      dataIndex: 'tonnage',
      sorter: (a, b) => safeNum(a.tonnage) - safeNum(b.tonnage),
      render: (text) => extractInteger(text),
      ...getNumericFilterProps('tonnage'),
    },
    {
      title: 'Shot Size (oz)',
      dataIndex: 'shotsize',
      sorter: (a, b) => safeNum(a.shotsize) - safeNum(b.shotsize),
      render: (text) => extractInteger(text),
      ...getNumericFilterProps('shotsize'),
    },
    {
      title: `Tie Bar Distance (Horizontal) (${unit})`,
      dataIndex: 'tieBarDistanceH',
      sorter: (a, b) => safeNum(a.tieBarDistanceH) - safeNum(b.tieBarDistanceH),
      render: (text) => formatWithUnit(text, unit),
      ...getNumericFilterProps('tieBarDistanceH'),
    },
    {
      title: `Tie Bar Distance (Vertical) (${unit})`,
      dataIndex: 'tieBarDistanceV',
      sorter: (a, b) => safeNum(a.tieBarDistanceV) - safeNum(b.tieBarDistanceV),
      render: (text) => formatWithUnit(text, unit),
      ...getNumericFilterProps('tieBarDistanceV'),
    },
    {
      title: `Mold Height Minimum (${unit})`,
      dataIndex: 'moldheightMin',
      sorter: (a, b) => safeNum(a.moldheightMin) - safeNum(b.moldheightMin),
      render: (text) => formatWithUnit(text, unit),
      ...getNumericFilterProps('moldheightMin'),
    },
    {
      title: `Mold Height Maximum (${unit})`,
      dataIndex: 'moldheightMax',
      sorter: (a, b) => safeNum(a.moldheightMax) - safeNum(b.moldheightMax),
      render: (text) => formatWithUnit(text, unit),
      ...getNumericFilterProps('moldheightMax'),
    },
    {
      title: 'Serial Number',
      dataIndex: 'serialNumber',
      filteredValue: null,
      sorter: (a, b) => safeStr(a.serialNumber).localeCompare(safeStr(b.serialNumber)),
    },
    {
      title: 'Number of Cores',
      dataIndex: 'numberofCores',
      filteredValue: null,
      sorter: (a, b) => safeNum(a.numberofCores) - safeNum(b.numberofCores),
    },
    {
      title: 'Number of Valve Gates',
      dataIndex: 'numberofValveGates',
      filteredValue: null,
      sorter: (a, b) => safeNum(a.numberofValveGates) - safeNum(b.numberofValveGates),
    },
    {
      title: 'Number of Air Blows',
      dataIndex: 'numberofAirBlows',
      filteredValue: null,
      sorter: (a, b) => safeNum(a.numberofAirBlows) - safeNum(b.numberofAirBlows),
    },
    {
      title: 'Lane',
      dataIndex: 'lane',
      filteredValue: null,
      sorter: (a, b) => safeNum(a.lane) - safeNum(b.lane),
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'id',
      filteredValue: null,
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action justify-content-start">
            <Link className="me-2 p-2" onClick={() => handleEditClick(record)}>
              <i data-feather="edit" className="feather-edit"></i>
            </Link>
            {/* <Link className="confirm-text p-2" onClick={() => handleDeleteClick(record.id)}>
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link> */}
          </div>
        </div>
      ),
    },
  ], [filterStates, unit]);

  return (
    <div className="table-responsive Machine-list">
      {isLoading && <LoadingSpinner />}
      <Table
        columns={columns}
        dataSource={filteredTableData || []}
        rowKey={(record) => {
          // Use id as primary key, fallback to assetNumber or cellNumber
          if (record?.id != null) return `machine-${record.id}`;
          if (record?.assetNumber != null) return `machine-asset-${record.assetNumber}`;
          if (record?.cellNumber != null) return `machine-cell-${record.cellNumber}`;
          // Fallback to a unique key based on object reference
          return `machine-${Math.random().toString(36).slice(2, 11)}`;
        }}
      />
    </div>
  );
};

export default MachineTable;



// import React, { useEffect } from 'react';
// import { Table } from 'antd';
// import { Link } from 'react-router-dom';
// import { useSelector, useDispatch } from 'react-redux';
// import {
//   selectFilteredMachines,
//   selectMachineLoading,
//   selectfetchMachineLoading,
// } from './store/selectors';
// import { showModal } from 'Core/redux/main/slice';
// import { deleteMachine } from './store/slice';
// import { MODAL_TYPES } from 'Feature/components/app-modals';
// import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
// import { actions as machineActions } from './store/slice';

// const { setMachine } = machineActions;

// const { CELL_ADD_EDIT } = MODAL_TYPES;

// // Helper function to extract integer from value (removes units like mm, g, oz, etc.)
// const extractInteger = (value) => {
//   if (value === null || value === undefined || value === '') return '';
//   const stringValue = String(value).trim();
//   if (!stringValue) return '';
//   const match = stringValue.match(/(\d+(?:\.\d+)?)/);
//   if (match && match[1]) {
//     const num = parseFloat(match[1]);
//     if (!isNaN(num)) {
//       return Math.floor(num).toString();
//     }
//   }
//   return '';
// };

// const MachineTable = () => {
//   const dispatch = useDispatch();
//   const { filteredData, isLoading } = useSelector(state => ({
//     filteredData: selectFilteredMachines(state),
//     isLoading: selectMachineLoading(state) || selectfetchMachineLoading(state),
//   }));

//   const handleEditClick = record => {
//     dispatch(setMachine(record));
//     dispatch(showModal(CELL_ADD_EDIT, record));
//   };

//   const handleDeleteClick = id => {
//     dispatch(deleteMachine(id));
//   };

//   const handleCheckboxChange = (e, record) => {};

//   const initData = {
//     assetNumber: '',
//     cellNumber: '',
//     modelNumber: '',
//     tonnage: '',
//     shotsize: '',
//     tieBarDistanceH: '',
//     tieBarDistanceV: '',
//     moldheightMin: '',
//     moldheightMax: '',
//     serialNumber: '',
//     numberofCores: '',
//     numberofValveGates: '',
//     numberofAirBlows: '',
//     lane: '',
//     isDeleted: '',
//   };

//   const columns = [
//     {
//       title: 'Asset Number',
//       dataIndex: 'assetNumber',
//       sorter: (a, b) => a.assetNumber.length - b.assetNumber.length,
//     },
//     {
//       title: 'Cell Number',
//       dataIndex: 'cellNumber',
//       sorter: (a, b) => a.cellNumber.length - b.cellNumber.length,
//     },
//     {
//       title: 'Brand',
//       dataIndex: 'brand',
//       sorter: (a, b) => a.brand.length - b.brand.length,
//     },
//     {
//       title: 'Model Number',
//       dataIndex: 'modelNumber',
//       sorter: (a, b) => a.modelNumber.length - b.modelNumber.length,
//     },
//     {
//       title: 'Tonnage',
//       dataIndex: 'tonnage',
//       sorter: (a, b) => {
//         const aVal = extractInteger(a.tonnage);
//         const bVal = extractInteger(b.tonnage);
//         return (parseInt(aVal) || 0) - (parseInt(bVal) || 0);
//       },
//       render: (text, record) => extractInteger(text || record.tonnage),
//     },
//     {
//       title: 'Shot Size (oz)',
//       dataIndex: 'shotsize',
//       sorter: (a, b) => {
//         const aVal = extractInteger(a.shotsize);
//         const bVal = extractInteger(b.shotsize);
//         return (parseInt(aVal) || 0) - (parseInt(bVal) || 0);
//       },
//       render: (text, record) => extractInteger(text || record.shotsize),
//     },
//     {
//       title: 'Tie Bar Distance (Horizontal)',
//       dataIndex: 'tieBarDistanceH',
//       sorter: (a, b) => {
//         const aVal = extractInteger(a.tieBarDistanceH);
//         const bVal = extractInteger(b.tieBarDistanceH);
//         return (parseInt(aVal) || 0) - (parseInt(bVal) || 0);
//       },
//       render: (text, record) => extractInteger(text || record.tieBarDistanceH),
//     },
//     {
//       title: 'Tie Bar Distance (Vertical)',
//       dataIndex: 'tieBarDistanceV',
//       sorter: (a, b) => {
//         const aVal = extractInteger(a.tieBarDistanceV);
//         const bVal = extractInteger(b.tieBarDistanceV);
//         return (parseInt(aVal) || 0) - (parseInt(bVal) || 0);
//       },
//       render: (text, record) => extractInteger(text || record.tieBarDistanceV),
//     },
//     {
//       title: 'Mold Height Minimum',
//       dataIndex: 'moldheightMin',
//       sorter: (a, b) => {
//         const aVal = extractInteger(a.moldheightMin);
//         const bVal = extractInteger(b.moldheightMin);
//         return (parseInt(aVal) || 0) - (parseInt(bVal) || 0);
//       },
//       render: (text, record) => extractInteger(text || record.moldheightMin),
//     },
//     {
//       title: 'Mold Height Maximum',
//       dataIndex: 'moldheightMax',
//       sorter: (a, b) => {
//         const aVal = extractInteger(a.moldheightMax);
//         const bVal = extractInteger(b.moldheightMax);
//         return (parseInt(aVal) || 0) - (parseInt(bVal) || 0);
//       },
//       render: (text, record) => extractInteger(text || record.moldheightMax),
//     },
//     {
//       title: 'Serial Number',
//       dataIndex: 'serialNumber',
//       sorter: (a, b) => a.serialNumber.length - b.serialNumber.length,
//     },
//     {
//       title: 'Number of Cores',
//       dataIndex: 'numberofCores',
//       sorter: (a, b) => a.numberofCores.length - b.numberofCores.length,
//     },
//     {
//       title: 'Number of Valve Gates',
//       dataIndex: 'numberofValveGates',
//       sorter: (a, b) => a.numberofValveGates.length - b.numberofValveGates.length,
//     },
//     {
//       title: 'Number of Air Blows',
//       dataIndex: 'numberofAirBlows',
//       sorter: (a, b) => a.numberofAirBlows.length - b.numberofAirBlows.length,
//     },
//     {
//       title: 'Lane',
//       dataIndex: 'lane',
//       sorter: (a, b) => a.lane.length - b.lane.length,
//     },
//     {
//       title: 'Actions',
//       dataIndex: 'id',
//       key: 'id',
//       render: (text, record) => (
//         <div className="action-table-data">
//           <div className="edit-delete-action justify-content-start">
//             <Link className="me-2 p-2" onClick={() => handleEditClick(record)}>
//               <i data-feather="edit" className="feather-edit"></i>
//             </Link>
//             {/* <Link className="confirm-text p-2" onClick={() => handleDeleteClick(record.id)}>
//               <i data-feather="trash-2" className="feather-trash-2"></i>
//             </Link> */}
//           </div>
//         </div>
//       ),
//     },
//   ];

//   return (
//     <div className="table-responsive Machine-list">
//       {isLoading && <LoadingSpinner />}
//       <Table columns={columns} dataSource={filteredData} />
//     </div>
//   );
// };

// export default MachineTable;
